import { useState, useRef, useEffect } from 'react';

interface Option {
value: string;
label: string;
}

interface MultiSelectSearchProps {
options: Option[] | string[];
selectedValues: string[];
onSelectionChange: (values: string[]) => void;
placeholder?: string;
label?: string;
isLoading?: boolean;
itemLabel?: string; // e.g. "market", "media type" — used in button text
}

export function MultiSelectSearch({
options,
selectedValues,
onSelectionChange,
placeholder = 'Search...',
label,
isLoading = false,
itemLabel = 'market',
}: MultiSelectSearchProps) {
const [searchTerm, setSearchTerm] = useState('');
const [isOpen, setIsOpen] = useState(false);
const containerRef = useRef<HTMLDivElement>(null);
const buttonRef = useRef<HTMLButtonElement>(null);

// Normalize options to always have value/label structure
const normalizedOptions: Option[] = options.map(opt => 
typeof opt === 'string' ? { value: opt, label: opt } : opt
);

// Filter options based on search term (search both value and label)
// Make search more forgiving - trim, lowercase, and match anywhere in the string
const filteredOptions = normalizedOptions.filter((option) => {
const searchLower = searchTerm.toLowerCase().trim();
const labelLower = option.label.toLowerCase();
const valueLower = option.value.toLowerCase();

// Match if search term appears anywhere in label or value
return labelLower.includes(searchLower) || valueLower.includes(searchLower);
});

// Get labels for selected values
const getLabel = (value: string) => {
const option = normalizedOptions.find(opt => opt.value === value);
return option?.label || value;
};

// Close dropdown when clicking outside
useEffect(() => {
const handleClickOutside = (event: MouseEvent) => {
if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
setIsOpen(false);
setSearchTerm('');
}
};

document.addEventListener('mousedown', handleClickOutside);
return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

const handleToggleOption = (value: string) => {
if (selectedValues.includes(value)) {
onSelectionChange(selectedValues.filter((v) => v !== value));
} else {
onSelectionChange([...selectedValues, value]);
}
};

const handleToggleDropdown = () => {
setIsOpen(!isOpen);
if (!isOpen) {
setSearchTerm('');
}
};

const handleApply = () => {
setIsOpen(false);
setSearchTerm('');
};

const handleClearAll = () => {
onSelectionChange([]);
};

const pluralLabel = itemLabel.endsWith('s') ? itemLabel : `${itemLabel}s`;

const getButtonText = () => {
if (selectedValues.length === 0) {
return `Select ${pluralLabel}`;
}
if (selectedValues.length === 1) {
return selectedValues[0] ? getLabel(selectedValues[0]) : "";
}
return `${selectedValues.length} ${pluralLabel} selected`;
};

return (
<div ref={containerRef} className="relative">
{label && (
<label className="block text-sm font-medium text-neutral-600 mb-1">
{label}
</label>
)}

{/* Trigger button */}
<button
ref={buttonRef}
type="button"
onClick={handleToggleDropdown}
disabled={isLoading}
className="flex h-10 w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
>
<span className={selectedValues.length === 0 ? 'text-neutral-600' : 'text-neutral-900'}>
{isLoading ? 'Loading...' : getButtonText()}
</span>
<svg
className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
</svg>
</button>

{/* Dropdown panel */}
{isOpen && !isLoading && (
<div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-md ">
{/* Search input */}
<div className="p-3 border-b border-neutral-200">
<div className="relative">
<svg
className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
/>
</svg>
<input
type="text"
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
placeholder={placeholder}
className="w-full pl-10 pr-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
autoFocus
/>
</div>
</div>

{/* Options list */}
<div className="max-h-60 overflow-y-auto">
{filteredOptions.length > 0 ? (
<ul className="py-1">
{filteredOptions.map((option) => {
const isSelected = selectedValues.includes(option.value);
return (
<li key={option.value}>
<button
type="button"
onClick={() => handleToggleOption(option.value)}
className="w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 flex items-center gap-3"
>
<div
className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
isSelected
? 'bg-outfront-purple border-outfront-purple'
: 'border-neutral-200 bg-white'
}`}
>
{isSelected && (
<svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
<path
fillRule="evenodd"
d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
clipRule="evenodd"
/>
</svg>
)}
</div>
<span className="flex-1">{option.label}</span>
</button>
</li>
);
})}
</ul>
) : (
<div className="px-4 py-8 text-center text-sm text-neutral-600">
{searchTerm ? 'No matches found' : 'No options available'}
</div>
)}
</div>

{/* Footer with actions */}
<div className="p-3 border-t border-neutral-200 flex items-center justify-between gap-2">
{selectedValues.length > 0 && (
<button
type="button"
onClick={handleClearAll}
className="text-sm text-neutral-600 hover:text-neutral-900 underline"
>
Clear all
</button>
)}
<button
type="button"
onClick={handleApply}
className="ml-auto px-4 py-2 bg-outfront-purple text-white text-sm font-medium rounded-md hover:bg-outfront-purple-dark focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:ring-offset-2"
>
{selectedValues.length > 0
? `Apply (${selectedValues.length})`
: 'Close'}
</button>
</div>
</div>
)}
</div>
);
}
